import React, { useEffect, useState } from 'react'
import { Alert, InputGroup, Spinner } from 'react-bootstrap'
import FormControlWithToolTip from '../../components/kyc/FormControlWithToolTip'
import ScreenHeader from '../../components/kyc/ScreenHeader'
import 'react-calendar/dist/Calendar.css'
import { validateIdNumber } from '../../utils/Validation'
import {
  INVALID_ID_WARNING,
  SANCTION_SCREENING_OPTIONS,
  PRICELIST,
} from '../../utils/Constants'
import SearchBySelect from '../../components/form/SearchBySelect/SearchBySelect'
import FooterButtons from '../../components/form/FooterButtons/FooterButtons'
import FormInput from '../../components/form/FormInput/FormInput'
import {
  handleSanctionScreeningDownloadReport,
  handleSanctionScreeningLoadReport,
  handleSanctionScreeningSearchConsumer,
} from '../../api/sanctionScreening/sanctionScreening'
import {
  handleWorldComplianceSearchCompany,
  handleWorldComplianceLoadReport,
  handleWorldComplianceDownloadReport,
} from '../../api/worldCompliance/worldCompliance'
import TableDataSanctionScreening from '../../components/SanctionScreening/TableDataSanctionScreening'
import NewAndCloseButtons from '../../components/form/NewAndCloseButtons/NewAndCloseButtons'
import KycButton from '../../components/form/Button/Button'
import TableDataWorldCompliance from '../../components/WorldCompliance/TableDataWorldCompliance'
import ScreenLayout from '../../components/layouts/ScreenLayout'
import '../../components/kyc/kycStyle.css'
import { useDownloadFile } from '../../hooks/use-download-file'
import { ReportTypes } from '../../api/apiConstants'
import { ReactDatePicker } from '../../components/kyc/ReactDatePicker'
import moment from 'moment'

type sanctionScreeningConsumer = {
  firstname?: string
  surname?: string
  idno?: string
  name?: string
  enquiryID?: string
  enquiryResultID?: string
}
type sanctionScreeningCompany = {
  country?: string
  enquiryId?: string
  enquiryResultId?: string
  businessName?: string
}

interface SanctionScreeningProps {
  setShow: (value: boolean) => void
  setIsError: (value: boolean) => void
  setErrorMessage: (value: string) => void
  initialState: { [key: string]: any }
  setInitialState: React.Dispatch<React.SetStateAction<any>>
}

const SanctionScreening: React.FC<SanctionScreeningProps> = ({
  setShow,
  setIsError,
  setErrorMessage,
  setInitialState,
  initialState,
}) => {
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [showProgress, setShowProgress] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [country, setCountry] = useState('')
  const [surname, setSurname] = useState('')
  const [forenames, setForenames] = useState('')
  const [idNumber, setIdNumber] = useState('')
  const [dob, setDob] = useState<Date | null>(null)
  const [businessName, setBusinessName] = useState('')
  const [reference, setReference] = useState('')
  const [searchBySelectedOption, setSearchBySelectedOption] = useState(
    SANCTION_SCREENING_OPTIONS.searchBy[1].value,
  )
  const [companyName, setCompanyName] = useState('')
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [isMounted, setIsMounted] = useState(false)

  const [sanctionScreeningCompanies, setSanctionScreeningCompanies] = useState<
    sanctionScreeningCompany[]
  >([])
  const [sanctionScreeningCompany, setSanctionScreeningCompany] =
    useState<sanctionScreeningCompany>({})

  const [sanctionScreeningConsumers, setSanctionScreeningConsumers] = useState<
    sanctionScreeningConsumer[]
  >([])
  const [sanctionScreeningConsumer, setSanctionScreeningConsumer] =
    useState<sanctionScreeningConsumer>({})
  const [reportId, setReportId] = useState('0')

  const updateInitialState = () => {
    setInitialState({
      ...initialState,
      sanctionScreening: {
        country: country,
        surname: surname,
        forenames: forenames,
        reference: reference,
        idNumber: idNumber,
        dob: dob,
        companyName: companyName,
        registrationNumber: registrationNumber,
        searchBySelectedOption: searchBySelectedOption,
        isMounted: isMounted,
      },
    })
  }

  const { mutate: downloadReport, isLoading: isDownloading } = useDownloadFile(
    '/SanctionScreening/download-report',
    reportId,
    ReportTypes.SanctionScreeningReport.reportTypeId,
    setIsError,
    setErrorMessage,
  )

  const handleSanctionDownloadReport = () => {
    return downloadReport(reportId)
  }
  const handleSanctionScreeningSearch = () => {
    updateInitialState()
    const missing = []

    if (searchBySelectedOption === 'COMPANY') {
      if (!businessName) missing.push('Company / Trust Name')
      if (!country) missing.push('Country')
    } else if (searchBySelectedOption === 'INDIVIDUAL') {
      if (!idNumber) missing.push('Identity Number')
      if (!forenames) missing.push('Forenames')
      if (!surname) missing.push('Surname')
      if (!dob) missing.push('Date of Birth')
      if (!country) missing.push('Country')
    }
    setMissingFields(missing)
    if (missing.length > 0) {
      alert('Please fill in the following fields: ' + missing.join(', '))
      return
    }
    switch (searchBySelectedOption) {
      case 'COMPANY':
        handleWorldComplianceSearchCompany({
          businessName: businessName,
          reference,
          country,
          setShowProgress,
          setIsError,
          setErrorMessage,
          setSanctionScreeningCompanies,
        })
        break
      case 'INDIVIDUAL':
        handleSanctionScreeningSearchConsumer({
          identityNumber: idNumber,
          firstName: forenames,
          lastName: surname,
          dateOfBirth: moment(dob).format('YYYY-MM-DD'),
          reference,
          country,
          setShowProgress,
          setIsError,
          setErrorMessage,
          setSanctionScreeningConsumers,
        })
        break
      default:
        break
    }
  }

  const loadConsumerReport = async (consumer: sanctionScreeningConsumer) => {
    const handleSanctionScreeningLoadReportParams = {
      identityNumber: idNumber,
      sanctionScreeningConsumer: consumer,
      reference,
      sanctionScreeningCompany,
      setShowProgress,
      setIsError,
      setErrorMessage,
      setReportId,
      downloadReport,
    }
    handleSanctionScreeningLoadReport(handleSanctionScreeningLoadReportParams)
  }

  const loadCompanyReport = async (company: sanctionScreeningCompany) => {
    const handleWorldComplianceLoadReportParams = {
      reference,
      sanctionScreeningCompany: company,
      setErrorMessage,
      setIsError,
      setShowProgress,
      setReportId,
      downloadReport,
    }
    handleWorldComplianceLoadReport(handleWorldComplianceLoadReportParams)
  }

  // useEffect(() => {
  //   if (Object.keys(sanctionScreeningConsumer).length > 0)
  //     handleSanctionScreeningLoadReport(handleSanctionScreeningLoadReportParams)
  //   if (Object.keys(sanctionScreeningCompany).length > 0)
  //     handleWorldComplianceLoadReport(handleWorldComplianceLoadReportParams)
  // }, [sanctionScreeningConsumer, sanctionScreeningCompany])

  const handleStartOver = () => {
    setSearchBySelectedOption(SANCTION_SCREENING_OPTIONS.searchBy[1].value)
    setCountry('')
    setReference('')
    setReportId('0')
    setShowProgress(false)
    setReference('')
    setIdNumber('')
    setForenames('')
    setBusinessName('')
    setSurname('')
    setDob(null)
    setIsDisabled(false)
    setSanctionScreeningConsumers([])
    setSanctionScreeningConsumer({})
    setSanctionScreeningCompany({})
    setSanctionScreeningCompanies([])
  }
  return (
    <>
      {searchBySelectedOption === 'INDIVIDUAL' && (
        <>
          <ScreenLayout
            headerText={'Sanction Screening'}
            subHeaderText={
              'The World Compliance Search enables one to screen prospective clients and perform enhanced due diligence.'
            }
            price={PRICELIST.SANCTION_SCREENING}
            sampleReport={'Consumer_Screening.pdf'}
          >
            {showProgress && (
              <div className="spinner-container">
                <Spinner
                  as="span"
                  animation="border"
                  variant="dark"
                  role="status"
                  aria-hidden="true"
                ></Spinner>
                <span> </span>
              </div>
            )}
            {Object.keys(sanctionScreeningConsumers).length === 0 &&
              Object.keys(sanctionScreeningCompanies).length === 0 && (
                <>
                  {showProgress && (
                    <>
                      <div className="spinner-container">
                        <Spinner
                          as="span"
                          animation="border"
                          variant="dark"
                          role="status"
                          aria-hidden="true"
                        ></Spinner>
                        <span> </span>
                      </div>
                    </>
                  )}
                  <SearchBySelect
                    onChangeHandler={setSearchBySelectedOption}
                    options={SANCTION_SCREENING_OPTIONS.searchBy}
                    className="mb-3"
                  />
                  <FormControlWithToolTip
                    autoFocus={true}
                    onChangeHandler={setCountry}
                    placeHolderText="Country"
                    toolTipText={
                      'Will perform search within the provided country'
                    }
                    required
                    value={country}
                    className="mb-3 custom-input"
                  />

                  <FormControlWithToolTip
                    onChangeHandler={setSurname}
                    placeHolderText="Surname"
                    toolTipText={'Will perform search on last name'}
                    required
                    value={surname}
                    className="mb-3 custom-input"
                  />
                  <FormControlWithToolTip
                    onChangeHandler={setForenames}
                    placeHolderText="Forename(s)"
                    toolTipText={'Will perform search on forename'}
                    required
                    value={forenames}
                    className="mb-3 custom-input"
                  />
                  <FormControlWithToolTip
                    onChangeHandler={setIdNumber}
                    placeHolderText="ID number"
                    toolTipText={'Unique identity number'}
                    required
                    value={idNumber}
                    className="mb-3 custom-input"
                  />
                  {idNumber !== '' && !validateIdNumber(idNumber) ? (
                    <Alert variant="danger" transition={false}>
                      {INVALID_ID_WARNING}
                    </Alert>
                  ) : null}

                  <div
                    style={{ width: '100%', maxWidth: '430px', zIndex: 10 }}
                    className="mb-3"
                  >
                    <ReactDatePicker
                      selected={dob}
                      onChange={(date: Date) => setDob(date)}
                      placeholderText="Date of Birth (CCYY-MM-DD)"
                    />
                  </div>
                  {/* <FormControlWithToolTip
                    onChangeHandler={setDob}
                    placeHolderText="Date of Birth (CCYY-MM-DD)"
                    toolTipText={'Unique identity number'}
                    required
                    value={dob}
                    className="mb-3 custom-input"
                  /> */}

                  <FormInput
                    labelText="Reference"
                    placeHolderText="Search reference (optional)"
                    onChangeHandler={setReference}
                    value={reference}
                    className="mb-3"
                  />
                  <FooterButtons
                    setShow={setShow}
                    handleSearch={handleSanctionScreeningSearch}
                  />
                </>
              )}
            {Object.keys(sanctionScreeningConsumers).length > 0 && (
              <>
                <TableDataSanctionScreening
                  sanctionScreeningConsumers={sanctionScreeningConsumers}
                  setSanctionScreeningConsumer={loadConsumerReport}
                />
                <div className="mt-5">
                  <NewAndCloseButtons
                    handleAction={handleStartOver}
                    handleClose={setShow}
                  />
                </div>
              </>
            )}
          </ScreenLayout>
        </>
      )}
      {searchBySelectedOption === 'COMPANY' && (
        <>
          <ScreenLayout
            headerText={'Sanction Screening'}
            subHeaderText={
              'The World Compliance Search enables one to screen prospective clients and perform enhanced due diligence.'
            }
            price={PRICELIST.SANCTION_SCREENING}
            sampleReport={'Commercial_Screening.pdf'}
          >
            {showProgress && (
              <div className="spinner-container">
                <Spinner
                  as="span"
                  animation="border"
                  variant="dark"
                  role="status"
                  aria-hidden="true"
                ></Spinner>
                <span> </span>
              </div>
            )}
            {Object.keys(sanctionScreeningConsumers).length === 0 &&
              Object.keys(sanctionScreeningCompanies).length === 0 && (
                <>
                  <SearchBySelect
                    onChangeHandler={setSearchBySelectedOption}
                    options={SANCTION_SCREENING_OPTIONS.searchBy}
                    className="mb-3"
                  />
                  <FormControlWithToolTip
                    autoFocus={true}
                    onChangeHandler={setCountry}
                    placeHolderText="Country"
                    toolTipText={
                      'Will perform search within the provided country'
                    }
                    required
                    value={country}
                    className="mb-3 custom-input"
                  />
                  <FormControlWithToolTip
                    onChangeHandler={setBusinessName}
                    placeHolderText="Company / Trust name:"
                    toolTipText={
                      'Will perform a search on Company / Trust name'
                    }
                    required
                    value={businessName}
                    className="mb-3 custom-input"
                  />
                  <FormInput
                    labelText="Reference"
                    placeHolderText="Search reference (optional)"
                    onChangeHandler={setReference}
                    value={reference}
                    className="mb-3"
                  />
                  <FooterButtons
                    setShow={setShow}
                    handleSearch={handleSanctionScreeningSearch}
                  />
                </>
              )}
            {Object.keys(sanctionScreeningCompanies).length > 0 && (
              <>
                <TableDataWorldCompliance
                  sanctionScreeningCompanies={sanctionScreeningCompanies}
                  setSanctionScreeningCompany={loadCompanyReport}
                />
                <div className="mt-5">
                  <NewAndCloseButtons
                    handleAction={handleStartOver}
                    handleClose={setShow}
                  />
                </div>
              </>
            )}
            {/* {parseInt(reportId) > 0 && (
              <>
                <div>
                  <KycButton
                    onClick={handleSanctionDownloadReport}
                    variant={'primary'}
                    children={'Download Report'}
                    isLoading={isDownloading}
                    size="lg"
                  />
                </div>
                <NewAndCloseButtons
                  handleAction={handleStartOver}
                  handleClose={setShow}
                />
              </>
            )} */}
          </ScreenLayout>
        </>
      )}
    </>
  )
}

export default SanctionScreening
